<template>
  <header class="dash-topbar">
    <div class="dash-topbar__left">
      <div class="dash-brand-mark">
        <div class="dash-brand-mark__icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        </div>
        <div>
          <span class="dash-eyebrow">指导师沟通平台</span>
          <h1>企微家长沟通监管看板</h1>
        </div>
      </div>

      <div v-if="directScopes.length" class="dash-scope-panel">
        <div class="dash-scope-panel__meta">
          <span class="dash-eyebrow">当前视角</span>
          <strong>{{ currentScopeLabel }}</strong>
          <small>{{ currentScopeHint }}</small>
        </div>
      </div>
    </div>

    <div class="dash-topbar__right">
      <div class="dash-camp-select">
        <div class="dash-camp-select__icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        </div>
        <select v-model="selectedCampId" class="dash-camp-select__input" @change="onCampChange">
          <option value="" disabled>请选择营期</option>
          <option v-for="camp in campPeriods" :key="camp.id" :value="camp.id">
            {{ camp.name }}
          </option>
        </select>
        <span v-if="activeCamp" class="dash-camp-badge">{{ activeCamp.status === 'ACTIVE' ? '进行中' : '已结束' }}</span>
      </div>

      <div class="dash-user-menu">
        <div class="dash-user-menu__avatar">{{ userStore.realName?.charAt(0) }}</div>
        <div class="dash-user-menu__info">
          <span class="dash-user-menu__name">{{ userStore.realName }}</span>
          <span class="dash-user-menu__role">{{ roleLabel }}</span>
        </div>
        <button type="button" class="dash-user-menu__logout" aria-label="退出登录" @click="handleLogout" title="退出登录">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        </button>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { getCampPeriods } from '@/api'

const router = useRouter()
const userStore = useUserStore()

const roleMap = {
  ADMIN: '系统管理员',
  CENTER_LEAD: '中心主任',
  DEPT_LEAD: '部门负责人',
  MANAGER: '组长'
}

const roleLabel = computed(() => roleMap[userStore.highestRole] || '')

const campPeriods = ref([])
const selectedCampId = ref('')
const selectedDirectScopeId = ref('')
const isDefaultScope = ref(true)

const activeCamp = computed(() => {
  return campPeriods.value.find(c => c.id === selectedCampId.value) || null
})

const directScopes = computed(() => {
  const priority = { CENTER: 3, DEPT: 2, GROUP: 1 }
  return [...userStore.orgScopes].sort((left, right) => {
    const typeDiff = (priority[right.orgType] || 0) - (priority[left.orgType] || 0)
    if (typeDiff !== 0) return typeDiff
    return (left.orgName || '').localeCompare(right.orgName || '')
  })
})

const currentDirectScope = computed(() => {
  return directScopes.value.find(scope => scope.orgUnitId === selectedDirectScopeId.value) || directScopes.value[0] || null
})

const currentScopeLabel = computed(() => {
  if (currentDirectScope.value) {
    return `${currentDirectScope.value.orgName} · ${scopeModeLabel.value}`
  }
  return '未配置负责范围'
})

const scopeModeLabel = computed(() => {
  if (userStore.highestRole === 'CENTER_LEAD') return '中心视角'
  if (userStore.highestRole === 'DEPT_LEAD') return '部门视角'
  if (userStore.highestRole === 'MANAGER') return '本组'
  return orgTypeLabel(currentDirectScope.value?.orgType)
})

const currentScopeHint = computed(() => {
  if (userStore.highestRole === 'CENTER_LEAD') return '部门排名与明细联动展示'
  if (userStore.highestRole === 'DEPT_LEAD') return '小组排名与明细联动展示'
  if (userStore.highestRole === 'MANAGER') return '仅展示当前小组数据'
  return '按账号权限展示数据'
})

const emit = defineEmits(['select-camp', 'scope-change'])

function onCampChange() {
  const camp = campPeriods.value.find(c => c.id === selectedCampId.value)
  if (camp) {
    emit('select-camp', camp)
  }
}

onMounted(async () => {
  if (directScopes.value.length > 0) {
    selectedDirectScopeId.value = directScopes.value[0].orgUnitId
    emitCurrentScope()
  }

  try {
    const res = await getCampPeriods()
    if (res.code === 200 && res.data) {
      campPeriods.value = res.data
      if (res.data.length > 0) {
        selectedCampId.value = res.data[0].id
        emit('select-camp', res.data[0])
      }
    }
  } catch (err) {
    console.error('获取营期列表失败', err)
  }

})

function handleLogout() {
  userStore.clearUser()
  router.push('/login')
}

function emitCurrentScope() {
  emit('scope-change', {
    orgUnitId: currentDirectScope.value?.orgUnitId || '',
    directScopeId: currentDirectScope.value?.orgUnitId || '',
    orgName: currentDirectScope.value?.orgName || '',
    orgType: currentDirectScope.value?.orgType || '',
    role: userStore.highestRole || '',
    isDefault: isDefaultScope.value
  })
}

function orgTypeLabel(type) {
  return {
    CENTER: '中心',
    DEPT: '部门',
    GROUP: '小组'
  }[type] || type || ''
}

</script>

<style scoped>
.dash-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  border: 1px solid rgba(29, 47, 44, 0.06);
  background: linear-gradient(135deg, rgba(255, 252, 248, 0.92), rgba(245, 239, 229, 0.88));
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(37, 56, 50, 0.08);
  backdrop-filter: blur(16px);
}

.dash-topbar__left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex: 1;
  flex-wrap: wrap;
}

.dash-topbar__right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.dash-brand-mark {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dash-scope-panel {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 14px;
  border: 1px solid rgba(34, 58, 53, 0.1);
  border-radius: 16px;
  background: rgba(255, 250, 244, 0.72);
  min-width: min(100%, 560px);
}

.dash-scope-panel__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.dash-scope-panel__meta strong {
  font-size: 0.88rem;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-scope-panel__meta small {
  color: var(--ink-soft);
  font-size: 0.68rem;
  line-height: 1.35;
}

.dash-brand-mark__icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(135deg, #296b73, #1f4e5a);
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(41, 107, 115, 0.25);
}

.dash-eyebrow {
  display: block;
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink-soft);
  margin-bottom: 1px;
}

.dash-topbar h1 {
  margin: 0;
  font-size: 1.15rem;
  font-family: "Avenir Next", "PingFang SC", "Microsoft YaHei UI", sans-serif;
  letter-spacing: -0.02em;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-camp-select {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  height: 40px;
  border: 1px solid rgba(34, 58, 53, 0.1);
  border-radius: 12px;
  background: rgba(255, 250, 244, 0.7);
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.dash-camp-select:focus-within {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px rgba(41, 107, 115, 0.1);
}

.dash-camp-select__icon {
  display: flex;
  align-items: center;
  color: var(--ink-soft);
  flex-shrink: 0;
}

.dash-camp-select__input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.82rem;
  color: var(--ink);
  font-family: inherit;
  cursor: pointer;
  min-width: 160px;
  padding: 0;
  -webkit-appearance: none;
  appearance: none;
}

.dash-camp-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  background: rgba(43, 139, 100, 0.1);
  color: #2b8b64;
  flex-shrink: 0;
  white-space: nowrap;
}

.dash-user-menu {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 5px 5px 6px;
  border: 1px solid rgba(34, 58, 53, 0.1);
  border-radius: 14px;
  background: rgba(255, 250, 244, 0.7);
  transition: border-color 160ms ease;
}

.dash-user-menu:hover {
  border-color: rgba(34, 58, 53, 0.18);
}

.dash-user-menu__avatar {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, #296b73, #1f4e5a);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 700;
  flex-shrink: 0;
  letter-spacing: 0.02em;
}

.dash-user-menu__info {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
}

.dash-user-menu__name {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.dash-user-menu__role {
  font-size: 0.62rem;
  color: var(--ink-soft);
  letter-spacing: 0.04em;
  line-height: 1.3;
}

.dash-user-menu__logout {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  color: var(--ink-soft);
  cursor: pointer;
  padding: 0;
  border-radius: 8px;
  flex-shrink: 0;
  transition: background 160ms ease, color 160ms ease;
}

.dash-user-menu__logout:hover {
  background: rgba(203, 97, 69, 0.1);
  color: var(--danger);
}

@media (max-width: 860px) {
  .dash-topbar {
    flex-direction: column;
    align-items: stretch;
    padding: 12px 14px;
    gap: 10px;
  }

  .dash-topbar__right {
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
  }

  .dash-scope-panel {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  }

  .dash-topbar h1 {
    font-size: 1rem;
  }

  .dash-camp-select__input {
    min-width: 120px;
    font-size: 0.78rem;
  }

  .dash-camp-badge {
    display: none;
  }

  .dash-user-menu__info {
    display: none;
  }

  .dash-user-menu {
    padding: 5px;
  }
}
</style>
